// src/components/ContentList.js
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Avatar
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  School as GradeLevelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ContentList = ({ contents, onRefresh, loading,  fromPublishedTab = false }) => {
  const navigate = useNavigate();
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Handle action menu
  const handleActionMenuOpen = (event, content) => {
    event.stopPropagation();
    setSelectedContent(content);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  // Handle row click to view details
  const handleRowClick = (content) => {
    const type = fromPublishedTab?'published':'draft'
    navigate(`/reading-practices/${type}/${content.id}`);
  };

  // Handle edit action
  const handleEdit = (event, content) => {
    const type = fromPublishedTab?'published':'draft'
    event.stopPropagation();
    handleActionMenuClose();
    navigate(`/reading-practices/${type}/${content.id}/edit`);
  };

  // Handle delete dialog
  const handleDeleteClick = () => {
    handleActionMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };



  // Helper function to get grade level
  const getGradeLevel = (content) => {
    return content.gradeLevel || 
           content.cover?.gradeLevel || 
           content.metadata?.gradeLevel ||
           'Not specified';
  };
  


  return (
    <>
      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 1, 
          boxShadow: 'none',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="reading practice content table">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>Content</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>Grade Level</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contents.map((content) => {
              
              // Get thumbnail image URL
              const thumbnailImageURL = content.thumbnailImage.url
              
              // Get title and category
              const title = content.title?.raw?.value || 'Untitled Content';
              const category = content.category?.raw?.value || 'Uncategorized';
              const synopsis = content.synopsis?.raw?.value || '';
              
              return (
                <TableRow
                  key={content.id}
                  hover
                  onClick={() => handleRowClick(content)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:last-child td, &:last-child th': { border: 0 },
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: 'background.subtle',
                      transform: 'scale(1.005)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={thumbnailImageURL}
                        variant="rounded"
                        sx={{ 
                          width: 40, 
                          height: 40,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                        alt={title}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {title}
                        </Typography>
                        {synopsis && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {synopsis}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={category} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ 
                        bgcolor: 'rgba(229, 9, 20, 0.1)',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <GradeLevelIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.6 }} />
                      <Typography variant="body2">{getGradeLevel(content)}</Typography>
                    </Box>
                  </TableCell>
            
                
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="View">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(content);
                          }}
                          sx={{ color: 'text.secondary' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleEdit(e, content)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="More options">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleActionMenuOpen(e, content)}
                          aria-label="more options"
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        
        {/* Loading indicator for infinite scroll */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Loading more items...
            </Typography>
          </Box>
        )}
      </TableContainer>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <MenuItem onClick={() => {
          handleActionMenuClose();
          if (selectedContent) handleRowClick(selectedContent);
        }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={(e) => {
          if (selectedContent) handleEdit(e, selectedContent);
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
                
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 1
          }
        }}
      >
        <DialogTitle id="delete-dialog-title">
          Delete Reading Practice Content
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete "{selectedContent?.cover?.title?.raw?.value || 'this content'}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContentList;